import { useRouter } from "next/router";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "next-themes";
import { useMemo, useState } from "react";
import Image from "next/image";
import TextLogo from "../../public/logo/text-logo.png";
import LogoIcon from "../../public/logo/logo.png";
import ScaleLogo from "../../public/logo/scale.png";
import {
  BrandSvg,
  CauseSvg,
  CampaignSvg,
  CollectionsSvg,
  ImpactARTSvg,
  DashboardSvg,
  ProfileSvg,
  SettingsSvg,
  LogoutSvg,
  MilestonesSvg,
  ReportsSvg,
  HomeSvg,
  AdminVerificationSvg,
  IdentityVerificationSvg,
  MyCampaignsSvg,
} from "../svg/svgFilesGlobal";
import { useRole } from "../../contexts/RoleContext";
import {
  MAIN_NAV_CONFIG,
  SECONDARY_NAV_CONFIG,
} from "../../config/navigation.config";
import { filterNavByRole } from "../../utils/navigationUtils";
import NotificationIcon from "./NotificationIcon";
import { useNotificationContext } from "../../contexts/NotificationContext";
import ExitConfirmationModal from "../shared/ExitConfirmationModal";

// Icon mapping (hoisted outside component for performance)
const NAV_ICONS = {
  "visit-main": <HomeSvg />,
  dashboard: <DashboardSvg />,
  campaigns: <CampaignSvg />,
  "my-campaigns": <MyCampaignsSvg />,
  brands: <BrandSvg />,
  causes: <CauseSvg />,
  collections: <CollectionsSvg />,
  "create-dot": <ImpactARTSvg />,
  "my-dots": <ImpactARTSvg />,
  milestones: <MilestonesSvg />,
  reports: <ReportsSvg />,
  updates: <ReportsSvg />,
  "approval-status": (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  "cause-campaign-approvals": <AdminVerificationSvg />,
  "admin-requests": <AdminVerificationSvg />,
  "admin-insights": (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
      <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
    </svg>
  ),
  "campaign-status": <CampaignSvg />,
  "admin-causes": <AdminVerificationSvg />,
  "admin-brands": <AdminVerificationSvg />,
  "admin-support": (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
    </svg>
  ),
  notifications: null,
  "identity-verification": <IdentityVerificationSvg />,
  profile: <ProfileSvg />,
  settings: <SettingsSvg />,
  feedback: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
  ),
};

const Sidebar = ({
  isOpen,
  isExpanded,
  isMobile,
  onClose,
  onSetExpanded,
  currentPath,
  user,
}) => {
  const router = useRouter();
  const { logout } = useAuth();
  const { resolvedTheme } = useTheme();
  const { userRole } = useRole();
  const { count: notificationCount } = useNotificationContext();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const filteredMainNavItems = useMemo(
    () => filterNavByRole(MAIN_NAV_CONFIG, userRole),
    [userRole]
  );

  const filteredSecondaryNavItems = useMemo(
    () => filterNavByRole(SECONDARY_NAV_CONFIG, userRole),
    [userRole]
  );

  const isActive = (href, external = false) => {
    if (external) return false;
    if (href === "/dashboard") return currentPath === "/dashboard";
    return currentPath === href || currentPath.startsWith(href + "/");
  };

  const handleNavigation = (href, external = false) => {
    if (external) {
      window.open(href, "_blank");
    } else {
      router.push(href);
    }
    onClose();
  };

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    window.location.href = "/sign-in";
  };

  const handleMouseEnter = () => {
    if (!isMobile) onSetExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!isMobile) onSetExpanded(false);
  };

  return (
    <>
    <aside
      className={`
        fixed top-0 left-0 z-50 h-full bg-white dark:bg-[#1F233F]
        transition-all duration-300 ease-in-out overflow-hidden
        ${isMobile ? "w-80" : isExpanded ? "w-[270px]" : "w-20"}
        ${isOpen || !isMobile ? "translate-x-0" : "-translate-x-full"}
        ${isMobile ? "shadow-xl" : "border-r border-jacarta-100 dark:border-jacarta-600"}
        ${!isMobile && isExpanded ? "shadow-xl" : ""}
      `}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-jacarta-100 dark:border-jacarta-600">
          <div className="flex items-center px-4 py-4">
            <div className="flex items-center space-x-2 overflow-hidden">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 relative">
                  <Image
                    src={LogoIcon}
                    alt="Impactoverse Logo"
                    width={32}
                    height={32}
                    className="object-contain"
                    priority
                  />
                </div>
              </div>

              {(isExpanded || isMobile) && (
                <div className="flex-shrink-0 transition-all duration-300">
                  <div className="w-32 h-8 relative">
                    <Image
                      src={resolvedTheme === "dark" ? ScaleLogo : TextLogo}
                      alt="Impactoverse Brand Logo"
                      width={128}
                      height={32}
                      className="object-contain"
                      priority
                    />
                  </div>
                </div>
              )}
            </div>

            {isMobile && (
              <button
                onClick={onClose}
                className="ml-auto p-2 rounded-lg hover:bg-jacarta-50 dark:hover:bg-jacarta-700 transition-colors duration-200"
              >
                <svg
                  className="w-5 h-5 text-jacarta-500 dark:text-jacarta-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 scrollbar-hide">
          {/* Main Navigation */}
          <div className="px-3 pr-7 space-y-1">
            {filteredMainNavItems.map((item) => (
              <div key={item.id} className="relative group">
                <button
                  onClick={() => handleNavigation(item.href, item.external)}
                  className={`
                    w-full flex items-center rounded-lg overflow-hidden
                    transition-colors duration-200 px-3 py-2
                    ${
                      isActive(item.href, item.external)
                        ? "bg-accent/10 text-accent dark:bg-accent/20 dark:text-accent"
                        : "text-grayish dark:text-jacarta-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700"
                    }
                  `}
                  title={!isExpanded && !isMobile ? item.title : undefined}
                >
                  <span className="flex-shrink-0 w-4 h-4 mr-3">
                    {NAV_ICONS[item.id]}
                  </span>
                  <span
                    className={`text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap ${
                      isExpanded || isMobile
                        ? "opacity-100 max-w-full"
                        : "opacity-0 max-w-0"
                    }`}
                  >
                    {item.title}
                  </span>
                </button>
                {!isExpanded && !isMobile && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-jacarta-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.title}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mx-3 mr-7 my-4 border-t border-jacarta-100 dark:border-jacarta-600" />

          {/* Secondary Navigation */}
          <div className="px-3 pr-7 space-y-1">
            {filteredSecondaryNavItems.map((item) => (
              <div key={item.id} className="relative group">
                <button
                  onClick={() => handleNavigation(item.href)}
                  className={`
                    w-full flex items-center rounded-lg overflow-hidden
                    transition-colors duration-200 px-3 py-2
                    ${
                      isActive(item.href)
                        ? "bg-accent/10 text-accent dark:bg-accent/20 dark:text-accent"
                        : "text-grayish dark:text-jacarta-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700"
                    }
                  `}
                  title={!isExpanded && !isMobile ? item.title : undefined}
                >
                  <span className="flex-shrink-0 w-4 h-4 mr-3 relative">
                    {item.id === "notifications" ? (
                      <NotificationIcon unreadCount={notificationCount} />
                    ) : (
                      NAV_ICONS[item.id]
                    )}
                    {item.id === "identity-verification" && user && !user.isEarthIdVerified && (
                      <span
                        data-testid="earthid-verification-dot"
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-[#1F233F]"
                      />
                    )}
                  </span>
                  <span
                    className={`text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap ${
                      isExpanded || isMobile
                        ? "opacity-100 max-w-full"
                        : "opacity-0 max-w-0"
                    }`}
                  >
                    {item.title}
                  </span>
                </button>
                {!isExpanded && !isMobile && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-jacarta-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.title}
                    {item.id === "notifications" && notificationCount > 0 && (
                      <span className="ml-1 text-green-400">
                        ({notificationCount})
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Logout */}
            <div className="relative group">
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="w-full flex items-center rounded-lg overflow-hidden text-grayish dark:text-jacarta-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700 transition-colors duration-200 px-3 py-2"
                title={!isExpanded && !isMobile ? "Logout" : undefined}
              >
                <span className="flex-shrink-0 w-4 h-4 mr-3">
                  <LogoutSvg />
                </span>
                <span
                  className={`text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap ${
                    isExpanded || isMobile
                      ? "opacity-100 max-w-full"
                      : "opacity-0 max-w-0"
                  }`}
                >
                  Logout
                </span>
              </button>
              {!isExpanded && !isMobile && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-jacarta-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  Logout
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* User Profile */}
        {user && (
          <div className="border-t border-jacarta-100 dark:border-jacarta-600 flex-shrink-0 overflow-hidden p-3">
            <div className="flex items-center space-x-3 overflow-hidden">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-accent/10 dark:bg-accent/20 flex items-center justify-center">
                  {user.profile?.profileImage ? (
                    <img
                      src={user.profile.profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-accent dark:text-accent font-medium text-sm">
                      {user.fullName?.charAt(0) ||
                        user.name?.charAt(0) ||
                        user.email?.charAt(0) ||
                        "U"}
                    </span>
                  )}
                </div>
              </div>
              <div
                className={`flex-1 min-w-0 overflow-hidden transition-all duration-300 ${
                  isExpanded || isMobile
                    ? "opacity-100 max-w-full"
                    : "opacity-0 max-w-0"
                }`}
              >
                <p className="text-sm font-medium text-grayish dark:text-white truncate whitespace-nowrap">
                  {user.fullName || user.name || "User"}
                </p>
                <p className="text-sm text-jacarta-400 dark:text-jacarta-300 truncate whitespace-nowrap">
                  {user.email || "johndoe@gmail.com"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>

    {/* Rendered outside the sidebar so the overlay is not clipped by it */}
    <ExitConfirmationModal
      isOpen={showLogoutConfirm}
      onClose={() => setShowLogoutConfirm(false)}
      onConfirm={handleLogout}
      title="Log out?"
      message="Are you sure you want to log out of your account?"
      confirmLabel="Yes, log out"
      cancelLabel="No, stay"
    />
    </>
  );
};

export default Sidebar;
